import { useEffect, useState } from "react";

/**
 * Accent theme, shared with the Trademark Intelligence dashboard.
 *
 * Same storage key, same origin (the dashboard is proxied under this domain),
 * so the two apps read and write one value. That is also what makes the sync
 * work without any messaging: `localStorage` raises a `storage` event in every
 * other tab on the origin, so changing the theme there repaints this app while
 * it is open in another tab.
 */

export const THEME_KEY = "entermark-theme";

/**
 * The accent each theme paints with.
 *
 * The slugs must be the ones the dashboard writes — an unknown value is left in
 * storage untouched and simply falls back here, so a mismatch degrades to the
 * default rather than overwriting the other app's choice.
 */
export const THEMES = {
  moody: { label: "Moody", accent: "#b4553f", soft: "#fbefec" },
  indigo: { label: "Indigo", accent: "#5b6ee8", soft: "#eef0fd" },
  emerald: { label: "Emerald", accent: "#10a37f", soft: "#eafaf5" },
  graphite: { label: "Graphite", accent: "#5b6472", soft: "#f1f3f6" },
};

/* What an untouched browser gets. Whatever the dashboard has already stored
   wins over this, since both apps read the one key. */
export const DEFAULT_THEME = "moody";

const safeRead = () => {
  try {
    return localStorage.getItem(THEME_KEY) || DEFAULT_THEME;
  } catch {
    // Private windows and blocked site data both throw rather than return null.
    return DEFAULT_THEME;
  }
};

/** Paints the accent. Everything else reads it through `var(--em-accent)`. */
export const applyTheme = (slug) => {
  const theme = THEMES[slug] || THEMES[DEFAULT_THEME];
  const root = document.documentElement;
  root.style.setProperty("--em-accent", theme.accent);
  root.style.setProperty("--em-accent-soft", theme.soft);
  root.setAttribute("data-em-theme", slug);
};

/**
 * The current theme, and a setter that both apps see.
 *
 * Re-read on focus as well as on the storage event: the event does not fire in
 * the tab that made the change, and a dashboard opened in the same tab (rather
 * than a new one) would otherwise come back to a stale accent.
 */
export const useTheme = () => {
  const [theme, setThemeState] = useState(safeRead);

  useEffect(() => { applyTheme(theme); }, [theme]);

  useEffect(() => {
    const sync = () => setThemeState(safeRead());
    const onStorage = (e) => { if (!e.key || e.key === THEME_KEY) sync(); };

    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", sync);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", sync);
    };
  }, []);

  const setTheme = (slug) => {
    try { localStorage.setItem(THEME_KEY, slug); } catch { /* nothing to do */ }
    setThemeState(slug);
  };

  return { theme, setTheme, themes: THEMES };
};
